import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaInstagram, FaLinkedin } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { motion } from "framer-motion";
import RoasberryLogo from "./components/RoasberryLogo";
import mehmetAvatar from "./foundersAvatar/mehmet.png";
import sinanAvatar from "./foundersAvatar/sinan.png";
import adnanAvatar from "./foundersAvatar/adnan.jpg";
import teamPhoto from "./foundersAvatar/team.png";
import heroBG from "./heroBG.svg";

function AcquisitionLanding() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Brand color
  const brandColor = "#E30B5C";

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const fadeIn = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.8 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const scaleIn = {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    transition: { duration: 0.5, ease: "easeOut" }
  };

  // Team member images
  const teamImages = [
    teamPhoto,
  ];

  const coFounders = [
    {
      name: "Mehmet Can İlker",
      title: "Co-Founder & CEO",
      description:
        "Responsible for strategy, leadership, and partnerships, Mehmet has guided Roasberry from the very beginning with a strong vision for growth and innovation.",
      image: mehmetAvatar,
      contact: "mailto:mehmet@roasberry.com",
    },
    {
      name: "Sinan Noyan",
      title: "Co-Founder & CPO",
      description:
        "As Chief Product Officer, Sinan led the product vision and user experience, ensuring Roasberry delivered real value to its customers.",
      image: sinanAvatar,
      contact: "mailto:sinan@roasberry.com",
    },
    {
      name: "Adnan Çobanoğlu",
      title: "Co-Founder & CTO",
      description:
        "Adnan managed all technical operations, building scalable and reliable infrastructure that powered Roasberry's analytics platform.",
      image: adnanAvatar,
      contact: "mailto:adnan@roasberry.com",
    },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Floating Navigation Header */}
      <nav className="fixed top-4 left-4 right-4 bg-white shadow-lg rounded-2xl z-50" style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <RoasberryLogo width={120} height={21} />
            </div>

            {/* Navigation Links - Desktop */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection("home")}
                className="text-gray-700 transition-colors font-medium"
                style={{
                  color: '#000',
                  ':hover': { color: brandColor }
                }}
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection("team-members")}
                className="text-gray-700 transition-colors font-medium"
                style={{
                  color: '#000',
                  ':hover': { color: brandColor }
                }}
              >
                Team Members
              </button>
              <button
                onClick={() => scrollToSection("co-founders")}
                className="text-gray-700 transition-colors font-medium"
                style={{
                  color: '#000',
                  ':hover': { color: brandColor }
                }}
              >
                Co-Founders
              </button>
              <a
                href="mailto:contact@roasberry.com"
                className="text-white px-6 py-2 rounded-lg transition-colors font-medium"
                style={{
                  backgroundColor: brandColor,
                  ':hover': { backgroundColor: '#c0094d' }
                }}
              >
                Contact Us
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <a
                href="mailto:contact@roasberry.com"
                className="text-white px-4 py-2 rounded-lg text-sm font-medium"
                style={{ backgroundColor: brandColor }}
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div>
        {/* Acquisition Announcement Section */}
        <section
          id="home"
          className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 relative overflow-hidden"
          style={{
            backgroundImage: `url(${heroBG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            paddingTop: '140px',
            paddingBottom: '80px'
          }}
        >
          {/* Optional overlay for better text readability */}
          <div className="absolute inset-0 bg-white/70"></div>

          <div className="max-w-5xl mx-auto text-center relative z-10 w-full" style={{ marginTop: '40px' }}>
            {/* Announcement Badge */}
            <motion.div
              className="inline-block mb-8"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="bg-gray-100 text-gray-700 px-6 py-2 rounded-full text-sm font-medium">
                We're excited to share some big news with you!
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span style={{ color: brandColor }}>Roasberry</span>{" "}
              <span className="text-gray-900">has been acquired by</span>
              <br />
              <span className="text-gray-900">Social Commerce Club!</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              As of December 2024, Roasberry officially exited and became part of
              Social Commerce Club.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-5 justify-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <motion.a
                href="https://socialcommerceclub.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white px-10 py-4 rounded-xl transition-colors font-semibold text-lg"
                style={{ backgroundColor: brandColor }}
                whileHover={{ scale: 1.05, backgroundColor: '#c0094d' }}
                whileTap={{ scale: 0.95 }}
              >
                Go to Social Commerce Club
              </motion.a>
              <motion.button
                onClick={() => scrollToSection("co-founders")}
                className="bg-white px-10 py-4 rounded-xl border-2 transition-colors font-semibold text-lg"
                style={{ color: brandColor, borderColor: brandColor }}
                whileHover={{ scale: 1.05, backgroundColor: '#fef2f7' }}
                whileTap={{ scale: 0.95 }}
              >
                Contact Co-Founders
              </motion.button>
            </motion.div>

            {/* Exit Success Badge */}
            <motion.div
              className="mb-12"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <p className="text-lg font-semibold text-gray-900 mb-6">
                A Successful Exit - Roasberry + SCC
              </p>
              <div className="flex items-center justify-center gap-6">
                {/* Roasberry Logo */}
                <motion.div
                  className="flex items-center gap-2"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1 }}
                >
                  <RoasberryLogo width={80} height={14} />
                </motion.div>
                {/* Plus Sign */}
                <motion.span
                  className="text-2xl font-bold"
                  style={{ color: brandColor }}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3, delay: 1.2 }}
                >
                  +
                </motion.span>
                {/* SCC Logo */}
                <motion.div
                  className="px-4 py-2 rounded"
                  style={{ backgroundColor: brandColor }}
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1 }}
                >
                  <span className="text-xl text-white font-bold">SCC</span>
                </motion.div>
              </div>
            </motion.div>

            {/* Description */}
            <motion.div
              className="max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.4 }}
            >
              <p className="text-gray-600 leading-relaxed text-lg">
                Roasberry has successfully completed its exit to Social Commerce
                Club. This marks the conclusion of our independent journey and
                the start of a new phase under Social Commerce Club. The
                Roasberry team is grateful for the trust and support of our
                community throughout this journey.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Team Members Section */}
        <section
          id="team-members"
          className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50"
        >
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                className="inline-block mb-4"
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <span className="bg-gray-200 text-gray-700 px-6 py-2 rounded-full text-sm">
                  Meet The People Behind Roasberry
                </span>
              </motion.div>
              <motion.h2
                className="text-3xl sm:text-4xl font-bold mb-4 text-[#222222]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Team Members of{" "}
                <span style={{ color: brandColor }}>Roasberry</span>
              </motion.h2>
              <motion.p
                className="text-gray-600 max-w-2xl mx-auto"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                At Roasberry, it's all about the people. Our team has worked
                side by side with energy and creativity, turning ideas into
                reality together.
              </motion.p>
            </motion.div>

            {/* Team Photo */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <motion.div
                className="aspect-video bg-gray-200 rounded-lg overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src={teamImages[currentImageIndex]}
                  alt="Roasberry Team"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.parentElement.innerHTML = `
                      <div class="w-full h-full flex items-center justify-center">
                        <p class="text-gray-400 text-lg">Team Photo</p>
                      </div>
                    `;
                  }}
                />
              </motion.div>
            </motion.div>

            {/* Image Carousel Dots */}
            <motion.div
              className="flex justify-center gap-2"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              {[...Array(10)].map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className="w-2 h-2 rounded-full transition-colors"
                  style={{ backgroundColor: index === 0 ? brandColor : '#fecdd3' }}
                  aria-label={`Go to image ${index + 1}`}
                  whileHover={{ scale: 1.5 }}
                  whileTap={{ scale: 0.9 }}
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.2, delay: 0.5 + index * 0.05 }}
                />
              ))}
            </motion.div>
          </div>
        </section>

        {/* Co-Founders Section */}
        <section id="co-founders" className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                className="inline-block mb-4"
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <span className="bg-gray-100 text-gray-700 px-6 py-2 rounded-full text-sm">
                  Meet The Founders Behind The Story
                </span>
              </motion.div>
              <motion.h2
                className="text-3xl sm:text-4xl font-bold mb-4 text-[#222222]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Co-Founders of <span style={{ color: brandColor }}>Roasberry</span>
              </motion.h2>
              <motion.p
                className="text-gray-600 max-w-2xl mx-auto"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                Meet the founders who turned an idea into a thriving product.
                Each of them has brought their unique expertise and dedication
                to shaping Roasberry's journey.
              </motion.p>
            </motion.div>

            {/* Co-Founder Cards */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, amount: 0.2 }}
            >
              {coFounders.map((founder, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-lg p-6 shadow-sm"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  whileHover={{
                    y: -10,
                    shadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                    transition: { duration: 0.3 }
                  }}
                >
                  {/* Profile Picture */}
                  <motion.div
                    className="mb-6 flex justify-center"
                    initial={{ scale: 0.5, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.2 + 0.2 }}
                  >
                    {founder.image ? (
                      <motion.img
                        src={founder.image}
                        alt={founder.name}
                        className="w-32 h-32 rounded-full object-cover"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                        onError={(e) => {
                          // If image fails to load, replace with placeholder
                          const placeholder = document.createElement("div");
                          placeholder.className =
                            "w-32 h-32 rounded-full flex items-center justify-center";
                          placeholder.style.backgroundColor = '#fef2f7';
                          placeholder.innerHTML = `<span class="text-4xl font-bold" style="color: ${brandColor}">${founder.name.charAt(
                            0
                          )}</span>`;
                          e.target.parentElement.replaceChild(
                            placeholder,
                            e.target
                          );
                        }}
                      />
                    ) : (
                      <motion.div
                        className="w-32 h-32 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: '#fef2f7' }}
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <span className="text-4xl font-bold" style={{ color: brandColor }}>
                          {founder.name.charAt(0)}
                        </span>
                      </motion.div>
                    )}
                  </motion.div>

                  {/* Name and Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-1 text-center">
                    {founder.name}
                  </h3>
                  <p className="font-medium mb-4 text-center" style={{ color: brandColor }}>
                    {founder.title}
                  </p>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-6 text-center leading-relaxed">
                    {founder.description}
                  </p>

                  {/* Contact Link */}
                  <div className="text-center">
                    <motion.a
                      href={founder.contact}
                      className="font-medium inline-flex items-center transition-colors"
                      style={{ color: brandColor }}
                      whileHover={{
                        x: 5,
                        color: '#c0094d'
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      Get in Touch
                      <motion.span
                        className="ml-1"
                        animate={{ x: [0, 3, 0] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        ›
                      </motion.span>
                    </motion.a>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-8">
              {/* Logo and Thank You Message */}
              <motion.div
                className="text-center md:text-left"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <motion.div
                  className="flex items-center justify-center md:justify-start mb-4"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <RoasberryLogo width={120} height={21} />
                </motion.div>
                <motion.p
                  className="text-gray-600 mb-6"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  Thank you for being part of the Roasberry story.
                </motion.p>

                {/* Social Media Icons */}
                <motion.div
                  className="flex gap-4 justify-center md:justify-start"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <motion.a
                    href="https://instagram.com/roasberry"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                    style={{ backgroundColor: '#fef2f7' }}
                    whileHover={{
                      scale: 1.1,
                      backgroundColor: '#fecdd3',
                      rotate: 5
                    }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaInstagram className="text-xl" style={{ color: brandColor }} />
                  </motion.a>
                  <motion.a
                    href="https://linkedin.com/company/roasberry"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                    style={{ backgroundColor: '#fef2f7' }}
                    whileHover={{
                      scale: 1.1,
                      backgroundColor: '#fecdd3',
                      rotate: 5
                    }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaLinkedin className="text-xl" style={{ color: brandColor }} />
                  </motion.a>
                  <motion.a
                    href="https://x.com/roasberry"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                    style={{ backgroundColor: '#fef2f7' }}
                    whileHover={{
                      scale: 1.1,
                      backgroundColor: '#fecdd3',
                      rotate: 5
                    }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaXTwitter className="text-xl" style={{ color: brandColor }} />
                  </motion.a>
                </motion.div>
              </motion.div>

              {/* Cookie Settings Link - Desktop */}
              <motion.div
                className="hidden md:block"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <motion.button
                  className="text-gray-600 transition-colors"
                  whileHover={{
                    color: brandColor,
                    scale: 1.05
                  }}
                >
                  Cookie Settings
                </motion.button>
              </motion.div>
            </div>

            {/* Copyright and Cookie Settings - Mobile */}
            <motion.div
              className="mt-8 pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p>© 2025 Roasberry. All rights reserved.</p>
              <motion.button
                className="md:hidden text-gray-600 transition-colors"
                whileHover={{
                  color: brandColor,
                  scale: 1.05
                }}
              >
                Cookie Settings
              </motion.button>
            </motion.div>
          </div>
        </footer>
      </div >
    </div >
  );
}

export default AcquisitionLanding;

